/**
 * Centralized logging utility for search operations
 * Provides consistent, debug-focused logging across all search components
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type SearchOperation = 'SEARCH' | 'CACHE' | 'API' | 'PLATFORM' | 'USER_DATA' | 'VALIDATION';

interface SearchContext {
  searchId: string;
  query: string;
  operation: SearchOperation;
  startTime?: number;
}

class SearchLogger {
  private static instance: SearchLogger;
  private searchContexts = new Map<string, SearchContext>();
  private isDevelopment = process.env.NODE_ENV === 'development';

  static getInstance(): SearchLogger {
    if (!SearchLogger.instance) {
      SearchLogger.instance = new SearchLogger();
    }
    return SearchLogger.instance;
  }

  /**
   * Start tracking a search operation
   */
  startSearch(query: string): string {
    const searchId = `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const context: SearchContext = {
      searchId,
      query: query.trim(),
      operation: 'SEARCH',
      startTime: performance.now()
    };
    
    this.searchContexts.set(searchId, context);
    this.log('info', 'SEARCH', `Starting search for: "${query}"`, { searchId });
    return searchId;
  }

  /**
   * End tracking a search operation with results summary
   */
  endSearch(searchId: string, resultCount: number, source: string): void {
    const context = this.searchContexts.get(searchId);
    if (!context || !context.startTime) return;

    const duration = performance.now() - context.startTime;
    this.log('info', 'SEARCH', 
      `Search completed: "${context.query}" → ${resultCount} results from ${source} (${duration.toFixed(0)}ms)`,
      { searchId, duration, resultCount, source }
    );
    
    this.searchContexts.delete(searchId);
  }

  /**
   * Log cache operations
   */
  cache(searchId: string, operation: 'hit' | 'miss' | 'set' | 'invalidate', details?: string): void {
    const context = this.searchContexts.get(searchId);
    const query = context?.query || 'unknown';
    
    const message = `Cache ${operation} for "${query}"${details ? `: ${details}` : ''}`;
    this.log('debug', 'CACHE', message, { searchId, operation, details });
  }

  /**
   * Log API operations
   */
  api(searchId: string, platform: string, operation: 'start' | 'success' | 'error', details?: any): void {
    const context = this.searchContexts.get(searchId);
    const query = context?.query || 'unknown';
    
    let level: LogLevel = 'debug';
    let message = '';
    
    switch (operation) {
      case 'start':
        message = `${platform} API call for "${query}"`;
        break;
      case 'success':
        const count = details?.count || 0;
        message = `${platform} API returned ${count} results for "${query}"`;
        break;
      case 'error':
        level = 'error';
        message = `${platform} API failed for "${query}": ${details?.error || 'Unknown error'}`;
        break;
    }
    
    this.log(level, 'API', message, { searchId, platform, operation, ...details });
  }

  /**
   * Log platform search operations
   */
  platform(searchId: string, platform: string, stage: 'start' | 'success' | 'fallback', details?: any): void {
    const context = this.searchContexts.get(searchId);
    const query = context?.query || 'unknown';
    
    let message = '';
    switch (stage) {
      case 'start':
        message = `Starting ${platform} search for "${query}"`;
        break;
      case 'success':
        const count = details?.count || 0;
        const duration = details?.duration || 0;
        message = `${platform} search: ${count} results (${duration}ms)`;
        break;
      case 'fallback':
        message = `Falling back from ${platform} search: ${details?.reason || 'No reason given'}`;
        break;
    }
    
    this.log('debug', 'PLATFORM', message, { searchId, platform, stage, ...details });
  }

  /**
   * Log user data operations
   */
  userData(searchId: string, operation: 'fetch' | 'match' | 'cache', details?: any): void {
    const context = this.searchContexts.get(searchId);
    const query = context?.query || 'unknown';
    
    let message = '';
    switch (operation) {
      case 'fetch':
        message = `Fetching user data for search "${query}"`;
        break;
      case 'match':
        const count = details?.count || 0;
        message = `Found ${count} user history matches for "${query}"`;
        break;
      case 'cache':
        message = `Using cached user data for "${query}"`;
        break;
    }
    
    this.log('debug', 'USER_DATA', message, { searchId, operation, ...details });
  }

  /**
   * Log validation operations
   */
  validation(searchId: string, operation: 'start' | 'pass' | 'fail', details?: any): void {
    const level = operation === 'fail' ? 'warn' : 'debug';
    const context = this.searchContexts.get(searchId);
    const query = context?.query || 'unknown';
    
    let message = '';
    switch (operation) {
      case 'start':
        message = `Validating cached results for "${query}"`;
        break;
      case 'pass':
        message = `Cache validation passed for "${query}"`;
        break;
      case 'fail':
        const reason = details?.reason || 'No reason given';
        message = `Cache validation failed for "${query}": ${reason}`;
        break;
    }
    
    this.log(level, 'VALIDATION', message, { searchId, operation, ...details });
  }

  /**
   * Log performance summary
   */
  performance(searchId: string, breakdown: Record<string, number>): void {
    const context = this.searchContexts.get(searchId);
    if (!context) return;

    const total = Object.values(breakdown).reduce((sum, time) => sum + time, 0);
    const stages = Object.entries(breakdown)
      .map(([stage, time]) => `${stage}: ${time.toFixed(0)}ms`)
      .join(', ');
    
    this.log('info', 'SEARCH', 
      `Performance breakdown for "${context.query}": ${stages} (total: ${total.toFixed(0)}ms)`,
      { searchId, breakdown, total }
    );
  }

  /**
   * Log errors with context
   */
  error(searchId: string, operation: SearchOperation, message: string, error?: any): void {
    const context = this.searchContexts.get(searchId);
    const query = context?.query || 'unknown';
    
    const fullMessage = `${operation} error for "${query}": ${message}`;
    this.log('error', operation, fullMessage, { searchId, error });
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, operation: SearchOperation, message: string, data?: any): void {
    if (!this.isDevelopment && level === 'debug') return;

    const timestamp = new Date().toISOString().slice(11, 23); // HH:mm:ss.SSS
    const logMessage = `[${timestamp}] [${operation}] ${message}`;

    switch (level) {
      case 'debug':
        console.debug(logMessage, data || '');
        break;
      case 'info':
        console.log(logMessage, data || '');
        break;
      case 'warn':
        console.warn(logMessage, data || '');
        break;
      case 'error':
        console.error(logMessage, data || '');
        break;
    }
  }
}

export const searchLogger = SearchLogger.getInstance();